// src/components/UserManagement.js

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield, faTrashAlt, faSpinner, faCheckCircle, faExclamationTriangle,
  faUsers, faUserPlus, faSort, faSortUp, faSortDown, faExternalLinkAlt,
  faEnvelopeCircleCheck // Icon for email verified
} from '@fortawesome/free-solid-svg-icons';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { db, functions } from '../firebase';
import { collection, getDocs, query, where, Timestamp, orderBy } from 'firebase/firestore';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';

const setUserAdminClaimCallable = httpsCallable(functions, 'setUserAdminClaim');
const deleteUserAuthCallable = httpsCallable(functions, 'deleteUserAuth');

// Helper function for sorting
const getSortableValue = (value) => {
  if (value instanceof Timestamp) {
    return value.toMillis(); // Sort by milliseconds for dates
  }
  if (typeof value === 'string') {
    return value.toLowerCase(); // Case-insensitive sort for strings
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0; // Sort booleans (true as 1, false as 0)
  }
  return value; // Numbers or other types
};


function UserManagement() {
  const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

  const [users, setUsers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [actionStatus, setActionStatus] = useState({ userId: null, type: null, loading: false });
  const [userMessage, setUserMessage] = useState({ type: '', text: '' });

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: 'email', direction: 'ascending' });

  const formatDate = (dateValue) => {
     const formatString = 'PP p';
     if (!dateValue) return 'N/A';
     try {
        let dateToFormat;
        if (dateValue instanceof Timestamp) {
            dateToFormat = dateValue.toDate();
        } else if (typeof dateValue === 'string') {
            dateToFormat = new Date(dateValue);
        } else if (dateValue && typeof dateValue._seconds === 'number' && typeof dateValue._nanoseconds === 'number') {
            dateToFormat = new Timestamp(dateValue._seconds, dateValue._nanoseconds).toDate();
        } else if (dateValue instanceof Date) {
            dateToFormat = dateValue;
        } else {
            return 'Invalid Date Input';
        }
        if (isNaN(dateToFormat.getTime())) return 'Invalid Date';
        return format(dateToFormat, formatString);
     } catch (e) {
        console.error("formatDate Error:", e, "Input:", dateValue);
        return 'Error Formatting Date';
     }
  };

  const fetchUsers = useCallback(async () => {
    if (!isAdmin || !userOrgId) {
        setLoadingData(false);
        return;
    }
    setLoadingData(true);
    setError(null);
    setUserMessage({ type: '', text: '' });

    try {
      const usersQuery = query(
          collection(db, 'users'),
          where('orgId', '==', userOrgId),
          orderBy('email', 'asc') // Default server-side sort
      );
      const userSnapshot = await getDocs(usersQuery);
      const resolvedUserList = userSnapshot.docs.map(docSnapshot => ({
          id: docSnapshot.id,
          ...docSnapshot.data()
      }));
      setUsers(resolvedUserList);
      setError(null);
    } catch (err) {
      console.error(`UserManagement: Error fetching users for org ${userOrgId}:`, err);
      if (err.code === 'permission-denied') { setError(`Permission denied fetching users. Check Firestore rules.`); }
      else if (err.message && err.message.includes("requires an index")) { setError("Database error: A required index is missing for the default sort (email). Please check your Firestore indexes."); }
      else { setError(err.message || "An unknown error occurred while fetching users."); }
      setUsers([]);
    } finally {
      setLoadingData(false);
    }
  }, [isAdmin, userOrgId]);

  useEffect(() => {
    if (!loadingAuth && isAdmin && userOrgId) {
        fetchUsers();
    } else if (!loadingAuth && (!isAdmin || !userOrgId)) {
        setLoadingData(false);
    }
  }, [loadingAuth, isAdmin, userOrgId, fetchUsers]);

  const sortedUsers = useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        const aValue = getSortableValue(a[sortConfig.key]);
        const bValue = getSortableValue(b[sortConfig.key]);

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        // For 'emailVerified' and 'admin', if values are the same, sort by email as a secondary criterion
        if (sortConfig.key === 'emailVerified' || sortConfig.key === 'admin') {
            const emailA = getSortableValue(a.email);
            const emailB = getSortableValue(b.email);
            if (emailA < emailB) return -1;
            if (emailA > emailB) return 1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return faSort;
    }
    return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
  };


  const handleToggleAdmin = async (userId, userEmail, currentAdminStatus) => {
      const makeAdmin = !currentAdminStatus;
      const actionText = makeAdmin ? 'grant admin privileges to' : 'remove admin privileges from';
      const selfAction = currentUser?.uid === userId;
      const confirmText = `Are you sure you want to ${actionText} ${selfAction ? 'YOURSELF' : userEmail}?`;

      if (!window.confirm(confirmText)) return;

      setActionStatus({ userId: userId, type: 'admin', loading: true });
      setUserMessage({ type: '', text: '' }); setError(null);

      try {
          const result = await setUserAdminClaimCallable({ userId: userId, setAdminStatus: makeAdmin });
          if (result?.data?.success) {
              setUsers(prevUsers =>
                  prevUsers.map(user =>
                      user.id === userId ? { ...user, admin: makeAdmin } : user
                  )
              );
              setUserMessage({ type: 'success', text: `Successfully ${makeAdmin ? 'granted' : 'removed'} admin privileges for ${userEmail}.` });
              if (selfAction) {
                   await currentUser?.getIdTokenResult(true);
              }
          } else {
              throw new Error(result?.data?.message || `Server denied the request to change admin status.`);
          }
      } catch (err) {
          console.error(`Error calling setUserAdminClaim function:`, err);
          setUserMessage({ type: 'danger', text: `Failed to update admin status: ${err.message}`});
      } finally {
          setActionStatus({ userId: null, type: null, loading: false });
      }
  };

  const handleDeleteUser = async (userId, userEmail) => {
       if (currentUser?.uid === userId) {
           setUserMessage({ type: 'warning', text: 'You cannot delete your own account.' });
           return;
       }
      const confirmText = `DELETE USER\n\nAre you sure you want to permanently delete ${userEmail}?\n\nWARNING: This action cannot be undone.`;
      if (!window.confirm(confirmText)) return;

      setActionStatus({ userId: userId, type: 'delete', loading: true });
      setUserMessage({ type: '', text: '' }); setError(null);

      try {
          const result = await deleteUserAuthCallable({ userId: userId });
          if (result?.data?.success) {
              setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
              setUserMessage({ type: 'success', text: `User "${userEmail}" deleted.` });
          } else {
              throw new Error(result?.data?.message || `Server denied the request to delete user.`);
          }
      } catch (err) {
          console.error(`Error calling deleteUserAuth function:`, err);
          setUserMessage({ type: 'danger', text: `Failed to delete user: ${err.message}`});
      } finally {
          setActionStatus({ userId: null, type: null, loading: false });
      }
  };

  // --- Render Logic ---

  if (loadingAuth) {
    return ( <div className="container text-center mt-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Verifying access...</span></div><p className="mt-2">Verifying access...</p></div> );
  }
  if (!currentUser) {
     return <Navigate to="/login" replace />;
  }
  if (!isAdmin) {
     return (
        <div className="container mt-3">
             <nav aria-label="breadcrumb" className="mb-3"><ol className="breadcrumb bg-light px-2 py-1 rounded-1 small"><li className="breadcrumb-item">Admin</li><li className="breadcrumb-item active" aria-current="page">Access Denied</li></ol></nav>
             <div className="alert alert-danger" role="alert"><h4>Access Denied</h4><p>You do not have permission to view this page.</p></div>
             <Link to="/dashboard" className="btn btn-secondary">Go to Dashboard</Link>
        </div>
     );
  }
  if (!userOrgId) {
       return (
        <div className="container mt-3">
             <nav aria-label="breadcrumb" className="mb-3"><ol className="breadcrumb bg-light px-2 py-1 rounded-1 small"><li className="breadcrumb-item">Admin</li><li className="breadcrumb-item active" aria-current="page">Configuration Error</li></ol></nav>
             <div className="alert alert-danger" role="alert"><h4>Configuration Error</h4><p>Your administrator account is missing necessary organization details. Cannot manage users.</p></div>
             <Link to="/dashboard" className="btn btn-secondary">Go to Dashboard</Link>
        </div>
     );
  }
   if (error) {
       return ( <div className="container mt-3"><div className="alert alert-danger" role="alert"><h4>Error Loading Users</h4><p>{error}</p></div></div> );
   }
   if (loadingData && users.length === 0) {
    return ( <div className="container text-center mt-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading user data...</span></div><p className="mt-2">Loading user data...</p></div> );
  }

  const totalUserCount = users.length;
  const iosUserCount = users.filter(user => user.platform && user.platform.toLowerCase() === 'ios').length;
  const androidUserCount = users.filter(user => user.platform && user.platform.toLowerCase() === 'android').length;
  const createUsersBaseUrl = "https://www.churchaware.com/create-account/";
  const familyBaseUrl = "https://www.churchaware.com/families/";


  return (
    <div className="container-fluid mt-3">
       <nav aria-label="breadcrumb" className="mb-3"><ol className="breadcrumb bg-light px-2 py-1 rounded-1 small">
          <li className="breadcrumb-item">Admin</li>
          <li className="breadcrumb-item active" aria-current="page">User Management</li>
       </ol></nav>
      <h2 className="mb-4 text-start">User Management</h2>
      {userMessage.text && ( <div className={`alert alert-${userMessage.type || 'info'} alert-dismissible fade show mx-1 mb-4`} role="alert">{userMessage.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} className="me-2" />}{userMessage.type === 'danger' && <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />}{userMessage.type === 'warning' && <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />}{userMessage.text}<button type="button" className="btn-close" onClick={() => setUserMessage({ type: '', text: '' })} aria-label="Close"></button></div> )}

      <div className="row mb-4 g-3">
          <div className="col-sm-6 col-md-3">
              <div className="card text-center shadow-sm h-100">
                  <div className="card-header bg-primary text-white">
                      <FontAwesomeIcon icon={faUsers} className="me-2" />Users
                  </div>
                  <div className="card-body d-flex flex-column justify-content-center">
                     <h4 className="card-title mb-0">{totalUserCount}</h4>
                  </div>
              </div>
          </div>
          <div className="col-sm-6 col-md-3">
              <div className="card text-center shadow-sm h-100">
                  <div className="card-header text-white" style={{ backgroundColor: '#007AFF' }}>
                      <FontAwesomeIcon icon={faAndroid} className="me-2" />Android
                  </div>
                  <div className="card-body d-flex flex-column justify-content-center">
                      <h4 className="card-title mb-0">{androidUserCount}</h4>
                  </div>
              </div>
          </div>
          <div className="col-sm-6 col-md-3">
              <div className="card text-center shadow-sm h-100">
                  <div className="card-header text-white" style={{ backgroundColor: '#007AFF' }}>
                      <FontAwesomeIcon icon={faApple} className="me-2" />Apple (iOS)
                  </div>
                  <div className="card-body d-flex flex-column justify-content-center">
                      <h4 className="card-title mb-0">{iosUserCount}</h4>
                  </div>
              </div>
          </div>
          <div className="col-sm-6 col-md-3">
              <div className="card text-center shadow-sm h-100">
                  <div className="card-header text-white" style={{ backgroundColor: '#45505d' }}>
                      <FontAwesomeIcon icon={faUserPlus} className="me-2" />Create Users
                  </div>
                  <div className="card-body d-flex flex-column justify-content-center">
                      <p className="card-text small mb-2">Share this link to add users:</p>
                      {userOrgId ? (
                          <code className="small" style={{ wordBreak: 'break-all' }}>
                              <a href={`${createUsersBaseUrl}${userOrgId}`} target="_blank" rel="noopener noreferrer">
                                  {`${createUsersBaseUrl}${userOrgId}`}
                              </a>
                          </code>
                      ) : (
                          <p className="text-muted small mb-0">Org ID not found.</p>
                      )}
                  </div>
              </div>
          </div>
      </div>

      {loadingData && sortedUsers.length === 0 ? (
        <div className="text-center mt-4">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading users...</span>
            </div>
            <p className="mt-2">Loading users...</p>
        </div>
      ) : sortedUsers.length === 0 && !loadingData ? (
        <div className="alert alert-info m-3 text-center">
            No users found in your organization ({userOrgId}).
        </div>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped table-hover table-sm align-middle mb-0 small">
                <thead className='table-light'>
                  <tr>
                    <th scope="col" onClick={() => requestSort('displayName')} style={{cursor: 'pointer'}}>
                        Display Name <FontAwesomeIcon icon={getSortIcon('displayName')} />
                    </th>
                    <th scope="col" onClick={() => requestSort('email')} style={{cursor: 'pointer'}}>
                        Email <FontAwesomeIcon icon={getSortIcon('email')} />
                    </th>
                    <th scope="col" className="text-center" onClick={() => requestSort('emailVerified')} style={{cursor: 'pointer'}}> {/* New Column Header */}
                        Email Verified <FontAwesomeIcon icon={getSortIcon('emailVerified')} />
                    </th>
                    <th scope="col" onClick={() => requestSort('creationTime')} style={{cursor: 'pointer'}}>
                        Registered <FontAwesomeIcon icon={getSortIcon('creationTime')} />
                    </th>
                    <th scope="col" onClick={() => requestSort('lastSignInTime')} style={{cursor: 'pointer'}}>
                        Last Sign In <FontAwesomeIcon icon={getSortIcon('lastSignInTime')} />
                    </th>
                    <th scope="col" className="text-center" onClick={() => requestSort('admin')} style={{cursor: 'pointer'}}>
                        Is Admin? <FontAwesomeIcon icon={getSortIcon('admin')} />
                    </th>
                    <th scope="col" onClick={() => requestSort('platform')} style={{cursor: 'pointer'}}>
                        Platform <FontAwesomeIcon icon={getSortIcon('platform')} />
                    </th>
                    <th scope="col" onClick={() => requestSort('userFid')} style={{cursor: 'pointer'}}>
                        User Family <FontAwesomeIcon icon={getSortIcon('userFid')} />
                    </th>
                    <th scope="col" className="text-center" style={{minWidth: '210px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user) => {
                    const isCurrentUser = currentUser?.uid === user.id;
                    const isLoadingThisUserAdmin = actionStatus.userId === user.id && actionStatus.type === 'admin' && actionStatus.loading;
                    const isLoadingThisUserDelete = actionStatus.userId === user.id && actionStatus.type === 'delete' && actionStatus.loading;
                    const isAdminUser = user.admin === true;
                    const isEmailVerified = user.emailVerified === true; // Ensure it's a boolean check

                    return (
                      <tr key={user.id}>
                        <td>{user.displayName || 'N/A'}</td>
                        <td style={{ wordBreak: 'break-all' }}>{user.email || '-'} {isCurrentUser && <span className="badge bg-info text-dark ms-1">You</span>}</td>
                        <td className="text-center"> {/* New Cell */}
                          {isEmailVerified ? (
                            <span className="badge bg-success" title="Email Verified">
                              <FontAwesomeIcon icon={faCheckCircle} /> Yes
                            </span>
                          ) : (
                            <span className="badge bg-danger" title="Email Not Verified">
                              <FontAwesomeIcon icon={faExclamationTriangle} /> No
                            </span>
                          )}
                        </td>
                        <td className="text-nowrap">{user.creationTime ? formatDate(user.creationTime) : (user.cDate ? formatDate(user.cDate) : '-')}</td>
                        <td className="text-nowrap">{user.lastSignInTime ? formatDate(user.lastSignInTime) : '-'}</td>
                        <td className="text-center">{isAdminUser ? <span className="badge bg-success">Yes</span> : <span className="badge bg-light text-dark">No</span>}</td>
                        <td className="text-nowrap">{user.platform || '-'}</td>
                        <td>
                          {user.userFid ? (
                            <a href={`${familyBaseUrl}${user.userFid}`} target="_blank" rel="noopener noreferrer" title={`View family (ID: ${user.userFid})`}>
                              Open Family <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="text-center text-nowrap">
                          {isLoadingThisUserAdmin || isLoadingThisUserDelete ? ( <FontAwesomeIcon icon={faSpinner} spin title="Processing..." /> ) : (
                            <>
                              <button
                                type="button"
                                className={`btn btn-sm py-0 px-2 me-1 ${isAdminUser ? 'btn-warning' : 'btn-success'}`}
                                title={isAdminUser ? `Remove admin privileges from ${user.email}` : `Make ${user.email} an admin`}
                                aria-label={isAdminUser ? `Remove admin privileges from ${user.email}` : `Make ${user.email} an admin`}
                                onClick={() => handleToggleAdmin(user.id, user.email, isAdminUser)}
                                disabled={isCurrentUser || isLoadingThisUserAdmin || isLoadingThisUserDelete}
                              >
                                <FontAwesomeIcon icon={faUserShield} fixedWidth />
                                <span className="ms-1 d-none d-md-inline">{isAdminUser ? 'Remove Admin' : 'Make Admin'}</span>
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-danger py-0 px-2"
                                title={`Delete user ${user.email}`}
                                aria-label={`Delete user ${user.email}`}
                                onClick={() => handleDeleteUser(user.id, user.email)}
                                disabled={isCurrentUser || isLoadingThisUserAdmin || isLoadingThisUserDelete}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} fixedWidth />
                                <span className="ms-1 d-none d-md-inline">Delete User</span>
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
